import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
      padding: 0 20px;
    }

    @media (max-width: 480px) {
      padding: 0 16px;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    @media (max-width: 480px) {
      grid-template-columns: 1fr; /* Single column on small screens */
      grid-gap: 8px;
    }

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      line-height: 1.4; /* Better readability */

      @media (max-width: 480px) {
        margin-bottom: 8px;
        padding-left: 16px;
      }

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--white-new);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--white-new);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--white-new);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'GeoAI & Spatial Machine Learning',
    'Google Earth Engine',
    'SAR/Optical Remote Sensing',
    'Geospatial Data Engineering',
    'Model-Data Fusion',
    'High-Performance Computing (HPC)',
    'Python, JS, R, SQL',
    'GIS (QGIS, ArcGIS)',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hi! I'm Waleed, a geospatial scientist passionate about solving real-world
              environmental problems with code. I build end-to-end, reproducible pipelines that
              translate complex satellite data into high-resolution insights on pressing issues like
              flood risk, land cover change, and urban heat stress.
            </p>

            <p>
              Currently, I'm a Ph.D. Candidate at{' '}
              <a href="https://geog.hkbu.edu.hk/" target="_blank" rel="noopener noreferrer">
                HKBU
              </a>{' '}
              and a{' '}
              <a href="https://g.dev/waleedgeo" target="_blank" rel="noopener noreferrer">
                Google Developer Expert in Earth Engine
              </a>
              . I’ve also had the privilege of working as a visiting scholar at{' '}
              <a
                href="https://urbanlab.kaust.edu.sa/people/mirza-waleed"
                target="_blank"
                rel="noopener noreferrer">
                KAUST
              </a>
              , publishing in{' '}
              <a
                href="https://scholar.google.com/citations?user=mx4VhG4AAAAJ"
                target="_blank"
                rel="noopener noreferrer">
                top journals
              </a>
              , and collaborating with stakeholders to turn complex analytics into policy-relevant
              solutions.
            </p>

            <p>
              My work exists at the intersection of satellite remote sensing, scalable machine
              learning, and high-performance computing, where my goal is to leverage GeoAI for
              tangible environmental impact.
            </p>
            <p>Here are a few technologies I’ve been working with recently:</p>
          </div>
          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
